import React, { useState, useRef, useEffect, useCallback } from "react";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import { useQuery, useApolloClient } from "@apollo/client";
import { useRouter } from "next/router";
import { Subject, from } from "rxjs";
import { debounceTime, switchMap, distinctUntilChanged } from "rxjs/operators";
import Skeleton from "react-loading-skeleton";
import { StoreEntityResponseCollection } from "gql-types";
import { globalStoresByCatName } from "gql/queries";
import { getStoreById } from "gql/queries/stores.gql";
import { formatStores, likesFormatter, rateFormatter } from "helpers";
import { searchStores } from "services/search/searchStores";
import { categoriesMap, categoryTranslateKey } from "helpers/constants/translate";
import { FiChevronLeft } from "react-icons/fi";
import { useGlobalState } from "context/GlobalStateContext";
import { MOBILE_MAX_WIDTH_SIZE, TABLET_MAX_WIDTH_SIZE } from "helpers/constants";
import KeywordsContainer from "./keywordsContainer";

export default function NavSearch({ categories: backupCategories, handleDisplaySearchBar }) {
  const client = useApolloClient();
  const router = useRouter();

  /* ──────────────────────────────   STATE   ────────────────────────────── */
  const [categories, setCategories] = useState(backupCategories ?? []);
  const [selectedCategory, setSelectedCategory] = useState(
    backupCategories?.[0]?.attributes.slug ?? null,
  );
  const [searchText, setSearchText] = useState("");
  const [stores, setStores] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  /* ──────────────────────────────   I18N   ─────────────────────────────── */
  const { t: tLayout } = useTranslation("layout");
  const { t: tCategories } = useTranslation("categories");

  /* ──────────────────────────────   REFS   ─────────────────────────────── */
  const wrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const previousScrollTopRef = useRef(0);
  const loadingRef = useRef<boolean>(false);
  const searchSubjectRef = useRef<Subject<string>>(new Subject());

  /* keep ref in-sync without re-subscribing */
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  /* ──────────────────────────────   GRAPHQL   ──────────────────────────── */
  const { refetch: apolloFetchStores } = useQuery<{ stores: StoreEntityResponseCollection }>(
    globalStoresByCatName,
    {
      variables: {
        filterOptions: { sub_category: { category: { slug: { eq: selectedCategory } } } },
        page,
      },
      notifyOnNetworkStatusChange: true,
      skip: true, // restoring it back to `!!searchText` will cause 2 request calls while scrolling down
    },
  );

  /* ──────────────────────────────   SIDE EFFECTS   ─────────────────────── */

  /* close popover on outside click */
  useEffect(() => {
    const handle = (e: MouseEvent) =>
      wrapperRef.current && !wrapperRef.current.contains(e.target as Node) && setOpen(false);

    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  /* sync categories prop */
  useEffect(() => {
    setCategories(backupCategories ?? []);
    setSelectedCategory(backupCategories?.[0]?.attributes.slug ?? null);
  }, [backupCategories]);

  /* debounced search – subscription created once */
  useEffect(() => {
    const sub = searchSubjectRef.current
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        switchMap((keyword) => {
          if (keyword) {
            return from(handleSearch(keyword, 1));
          } else {
            // When search is cleared, reset to category view
            setStores([]);
            setPage(1);
            setHasMore(true);
            return from(fetchCategoryStores(1, true));
          }
        }),
      )
      .subscribe();
    return () => sub.unsubscribe();
  }, [selectedCategory]);

  /* emit search events whenever the text changes */
  useEffect(() => {
    searchSubjectRef.current.next(searchText);
  }, [searchText]);

  /* infinite scroll – improved mobile support */
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const checkScrollPosition = () => {
      if (loadingRef.current || !hasMore) return;

      const { scrollTop, scrollHeight, clientHeight } = node;

      // Use a larger threshold for mobile (100px instead of 20px)
      const threshold = window.innerWidth < 768 ? 100 : 20;

      // Check if we're near the bottom
      if (scrollHeight - (scrollTop + clientHeight) < threshold) {
        setPage((prevPage) => prevPage + 1);
      }
    };

    // Throttled version of the scroll handler
    const throttledScrollHandler = throttle(200, checkScrollPosition);

    // Passive event listener for better performance
    const options: AddEventListenerOptions = { passive: true };

    // Add all relevant event listeners
    node.addEventListener("scroll", throttledScrollHandler, options);
    node.addEventListener("touchmove", throttledScrollHandler, options);
    node.addEventListener("wheel", throttledScrollHandler, options);

    return () => {
      node.removeEventListener("scroll", throttledScrollHandler, options);
      node.removeEventListener("touchmove", throttledScrollHandler, options);
      node.removeEventListener("wheel", throttledScrollHandler, options);
    };
  }, [hasMore]);

  // Simple throttle implementation
  function throttle(delay, fn) {
    let lastCall = 0;
    return function (...args) {
      const now = new Date().getTime();
      if (now - lastCall < delay) return;
      lastCall = now;
      return fn(...args);
    };
  }

  /* fetch when page increments (>1) */
  useEffect(() => {
    if (page === 1) return;

    if (searchText) {
      handleSearch(searchText, page);
    } else {
      fetchCategoryStores(page);
    }
  }, [page]);

  /* fetch when category changes */
  useEffect(() => {
    if (searchText || !selectedCategory) return; // Don't fetch categories when searching or no selectedcategory provided

    setStores([]);
    setPage(1);
    setHasMore(true);
    fetchCategoryStores(1, true);
    containerRef.current && (containerRef.current.scrollTop = 0);
  }, [selectedCategory]);

  //prevent body scrolling in table/mobile screens
  useEffect(() => {
    if (!window) {
      return;
    }
    if (window.innerWidth <= MOBILE_MAX_WIDTH_SIZE && open) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open]);

  /* ──────────────────────────────   CALLBACKS   ────────────────────────── */

  const fetchCategoryStores = useCallback(
    async (pageNum: number, reset = false) => {
      setLoading(true);

      try {
        const { data } = await apolloFetchStores({
          filterOptions: { sub_category: { category: { slug: { eq: selectedCategory } } } },
          pagination: { page: pageNum, pageSize: 8 },
        });

        const newStores = formatStores(data?.stores || []);

        setHasMore(newStores.length > 0);

        if (reset) {
          setStores(newStores);
        } else {
          setStores((prev) => [...prev, ...newStores]);
        }
      } catch (error) {
        console.error("Error fetching category stores:", error);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [selectedCategory, apolloFetchStores],
  );

  const handleSearch = useCallback(async (keyword: string, pageNum: number, reset = false) => {
    setLoading(true);

    try {
      const { stores: newStores = [] } = (await searchStores({ keyword, page: pageNum })) ?? {};
      setHasMore(newStores.length > 0);
      if (reset || pageNum === 1) {
        setStores(newStores);
      } else {
        setStores((prev) => [...prev, ...newStores]);
      }
    } catch (error) {
      console.error("Error searching stores:", error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const navigateToStore = useCallback(
    async (id: string, slug: string) => {
      resetSearch();
      const { data } = await client.query<{ stores: StoreEntityResponseCollection }>({
        query: getStoreById,
        variables: { filters: { id: { eq: id } } },
        fetchPolicy: "network-only",
      });
      const category =
        data.stores.data[0]?.attributes?.sub_category?.data?.attributes?.category?.data?.attributes
          ?.slug;
      await router.push(`/categories/${category}/${slug}`);
    },
    [client, router],
  );

  const resetSearch = useCallback(() => {
    setSearchText("");
    setOpen(false);
    setSelectedCategory(backupCategories?.[0]?.attributes.slug ?? null);
    setStores([]);
    setPage(1);
    setHasMore(true);
  }, [backupCategories]);

  const handleFocusSearch = () => {
    setOpen(true);
    // this causes to fetch stores whenever the searchbar is focused again
    // if (!searchText) {
    //   fetchCategoryStores(page);
    // }
  };

  /* ──────────────────────────────   RENDER   ───────────────────────────── */
  return (
    <div ref={wrapperRef} className="relative  max-w-4xl mx-auto my-1 lg:my-0">
      {/* Search input */}

      <div className="flex items-center ">
        <FiChevronLeft
          onClick={handleDisplaySearchBar}
          className="text-black cursor-pointer rtl:rotate-180  top-5 left-0 block lg:hidden"
          size={26}
        />
        <div className="flex xs:py-2 sm:p-0 bg-gray-100 rounded-md ">
          <div className="flex items-center ltr:pl-3 rtl:pr-3 pointer-events-none rtl:gap-2">
            <Image
              src="/images/searchIcon.png"
              alt="subscription icon"
              width={20}
              height={20}
              style={{
                maxWidth: "100%",
                height: "auto",
              }}
            />
            <div className="min-w-[5px] h-[35px] border-r-2 pl-2 border-gray-300"></div>
          </div>
          <input
            type="text"
            onFocus={() => handleFocusSearch()}
            placeholder={tLayout("search_seller")}
            className="rounded-md px-4 py-2 outline-none w-72 bg-gray-100 lg:h-12"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
      </div>

      {/* Popover dropdown */}
      <div className="flex justify-center relative ">
        <div
          className={`absolute h-screen md:h-auto pr-3 top-full left-1/2 -translate-x-1/2 mt-1 w-[95vw] md:w-fit bg-white rounded-xl shadow-xl border flex flex-col md:flex-row z-50 overflow-hidden transition-all ${
            open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          }`}
        >
          {/* Categories */}
          <div className="border-b md:border-b-0 md:border-r bg-gray-50 overflow-x-auto md:overflow-x-visible whitespace-nowrap w-full md:w-36 flex md:flex-col gap-2 px-2 py-2">
            <span className="hidden md:grid font-bold text-base md:py-4 text-center place-content-center">
              {tLayout("categories")}
            </span>
            {categories.map((cat) => (
              <button
                key={`${cat.attributes.slug}-${cat.id}`}
                onClick={() => {
                  setSearchText(""); // Clear search when selecting category
                  setSelectedCategory(cat.attributes.slug);
                }}
                className={`
                  cursor-pointer rounded-md px-4 py-2 text-sm whitespace-normal md:whitespace-nowrap
                  ${
                    selectedCategory === cat.attributes.slug
                      ? "bg-white font-semibold text-indigo-600 shadow"
                      : "hover:bg-gray-100"
                  }
                  flex-shrink-0
                `}
              >
                {tCategories(categoryTranslateKey(cat.attributes.slug))}
              </button>
            ))}
          </div>

          {/* Vendors list */}
          <div
            ref={containerRef}
            className="flex flex-col gap-4 vendors-list w-full min-w-[300px] md:w-[620px] p-4 h-screen md:h-auto max-h-screen md:max-h-[500px] overflow-y-auto"
          >
            <KeywordsContainer
              category={categoriesMap[selectedCategory]}
              className="self-center"
              handleSelect={(keyword) => setSearchText(keyword)}
              searchText={searchText}
            />
            {stores?.length === 0 && !loading ? (
              <div className="text-gray-400 text-sm px-4 py-2">
                {searchText ? tLayout("no_result_found") : tLayout("No_stores")}{" "}
              </div>
            ) : (
              stores.map((item, idx) => (
                <div
                  className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 rounded-lg py-2 mb-2 cursor-pointer"
                  onClick={() => {
                    navigateToStore(item?.id, item?.slug);
                  }}
                  key={`${item?.slug}-${idx}-${item?.id}`}
                >
                  <div className="flex items-center gap-4 md:min-w-fit">
                    <div className="self-center w-12 h-12  relative ltr:mr-2 rtl:ml-2 ltr:ml-2 rtl:mr-2">
                      <div className="relative w-10 h-10 rounded-full overflow-hidden border-2 border-orange-300 mx-2">
                        <Image
                          src={item?.imageUrl}
                          alt="micro-icon"
                          layout="fill"
                          objectFit="cover"
                        />
                      </div>

                      <div className="w-[18px] h-[18px] rounded-full absolute bottom-0 right-0 bg-white p-[2px]">
                        <Image
                          className="rounded-full"
                          src={`/images/micro-icon-${String(item?.platform ?? "").toLowerCase()}.svg`}
                          alt="delivery truck logo"
                          width={16}
                          height={16}
                        />
                      </div>
                    </div>
                    <div>
                      <span className="font-[Roboto] font-bold text-[#353535] whitespace-pre-line">
                        {item?.name?.length > 50
                          ? item.name
                              .split("_")
                              .join(" ")
                              .replace(/(.{50})/, "$1\n")
                          : item?.name?.split("_").join(" ")}
                      </span>
                      <div className="text-xs text-gray-500"></div>
                    </div>
                  </div>
                  <div className="flex gap-2 ltr:pr-4 rtl:pl-4">
                    {item?.reviewRate && (
                      <div className="flex items-center rtl:flex-row-reverse gap-1">
                        <span>
                          <svg
                            width="15"
                            height="13"
                            viewBox="0 0 15 13"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M7.35219 0L9.00286 4.68945H14.3445L10.023 7.58769L11.6737 12.2771L7.35219 9.3789L3.03068 12.2771L4.68135 7.58769L0.359842 4.68945H5.70152L7.35219 0Z"
                              fill="#353535"
                            />
                          </svg>
                        </span>
                        <span>{rateFormatter(item?.reviewRate)}</span>
                      </div>
                    )}
                    <div className="flex items-center rtl:flex-row-reverse gap-1">
                      <span className="rtl:scale-x-[-1]">
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.6761 6.07456C10.8988 5.78029 11.022 5.41975 11.022 5.04463C11.022 4.44948 10.6893 3.88613 10.1538 3.57198C10.016 3.49112 9.85899 3.44856 9.69916 3.44871H6.52058L6.60011 1.81965C6.61867 1.42598 6.47949 1.05218 6.20908 0.767195C6.07638 0.626727 5.91628 0.514966 5.73868 0.438819C5.56108 0.362671 5.36974 0.323754 5.17651 0.324473C4.48724 0.324473 3.8775 0.788403 3.69458 1.45249L2.55596 5.57484H2.55199V11.248H8.81239C8.93434 11.248 9.05364 11.2242 9.16365 11.1765C9.7946 10.9074 10.2015 10.291 10.2015 9.60706C10.2015 9.44004 10.1777 9.27568 10.13 9.11661C10.3526 8.82235 10.4759 8.46181 10.4759 8.08669C10.4759 7.91967 10.4521 7.75531 10.4043 7.59625C10.627 7.30198 10.7503 6.94144 10.7503 6.56632C10.7476 6.39931 10.7238 6.23362 10.6761 6.07456V6.07456ZM0.417908 5.999V10.8239C0.417908 11.0585 0.607456 11.248 0.842072 11.248H1.70366V5.57484H0.842072C0.607456 5.57484 0.417908 5.76439 0.417908 5.999Z"
                            fill="#353535"
                          />
                        </svg>
                      </span>
                      <span>{likesFormatter(item?.likesNbr || 0)}</span>
                    </div>
                  </div>
                </div>
              ))
            )}

            {loading &&
              Array.from(Array(4).keys()).map((_, index) => (
                <Skeleton
                  key={`__index__page__card__${index}`}
                  className="mb-2 bg-[#F2F2F2] rounded-lg h-16 flex items-center justify-between hover:bg-[#D9D9D9]"
                />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
